<script lang="ts">
  import { onMount } from 'svelte';
  import type { CommandSetSummary } from '@ucc/shared';
  import {
    ApiError,
    deleteCommandSet,
    fetchCommandSet,
    fetchCommandSets,
    replaceCommandSet,
    uploadCommandSet,
  } from '$lib/api';
  import { session } from '$lib/session.svelte';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  let commandSets = $state<CommandSetSummary[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let formError = $state<string | null>(null);
  let saving = $state(false);
  let newName = $state('');
  let detailId = $state<string | null>(null);
  let detailName = $state('');
  let detailJson = $state<string>('');
  let detailLoading = $state(false);

  const role = $derived(session.user?.role);
  const isStaff = $derived(role === 'admin' || role === 'user');

  onMount(() => {
    void reload();
  });

  async function reload() {
    loading = true;
    error = null;
    try {
      const res = await fetchCommandSets();
      commandSets = res.commandSets ?? [];
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Failed to load Command Sets';
      commandSets = [];
    } finally {
      loading = false;
    }
  }

  async function onUpload(e: Event) {
    e.preventDefault();
    formError = null;
    const name = newName.trim();
    if (!name) {
      formError = 'Name is required.';
      return;
    }
    const input = (e.target as HTMLFormElement).elements.namedItem('file') as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      formError = 'Choose a command.json file.';
      return;
    }
    saving = true;
    try {
      const text = await file.text();
      if (!text.trim()) {
        formError = 'File is empty.';
        return;
      }
      // Send the raw text — the server strips // and /* */ comments and gives a
      // specific, human-readable error (which command/reply, which value). A local
      // JSON.parse() here would reject any .jsonc with comments before the server
      // ever sees it, masking that better error behind a generic one.
      await uploadCommandSet({ name, json: text });
      newName = '';
      if (input) input.value = '';
      await reload();
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Upload failed';
    } finally {
      saving = false;
    }
  }

  async function openDetail(cs: CommandSetSummary) {
    detailId = cs.id;
    detailName = cs.name;
    detailJson = '';
    detailLoading = true;
    formError = null;
    try {
      const res = await fetchCommandSet(cs.id);
      detailJson = JSON.stringify(JSON.parse(res.commandSet.commandsJson), null, 2);
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Failed to load Command Set';
      detailId = null;
    } finally {
      detailLoading = false;
    }
  }

  async function onReplace(e: Event) {
    e.preventDefault();
    if (!detailId) return;
    formError = null;
    const input = (e.target as HTMLFormElement).elements.namedItem('replace') as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      formError = 'Choose a replacement command.json file.';
      return;
    }
    saving = true;
    try {
      const text = await file.text();
      if (!text.trim()) {
        formError = 'File is empty.';
        return;
      }
      await replaceCommandSet(detailId, { name: detailName.trim() || undefined, json: text });
      const id = detailId;
      await reload();
      const refreshed = commandSets.find((c) => c.id === id);
      if (refreshed) await openDetail(refreshed);
      if (input) input.value = '';
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Replace failed';
    } finally {
      saving = false;
    }
  }

  async function onDelete(cs: CommandSetSummary) {
    formError = null;
    const inUse = cs.skuCodes.length > 0;
    const ok = confirm(
      inUse
        ? `Delete Command Set "${cs.name}"?\n\nUsed by: ${cs.skuCodes.join(', ')}\n\nBlocked while attached to any SKU — detach in SKU Manager first.`
        : `Delete Command Set "${cs.name}"?`,
    );
    if (!ok) return;
    saving = true;
    try {
      await deleteCommandSet(cs.id);
      if (detailId === cs.id) {
        detailId = null;
        detailJson = '';
      }
      await reload();
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Delete failed';
    } finally {
      saving = false;
    }
  }
</script>

<div class="page">
  <a class="back" href="#/settings">← Settings</a>
  <h2>Command Sets</h2>
  <p class="sub">
    OTA command.json library — one file per SKU, firmware-authored. Upload and replace here; attach
    to a SKU in <a href="#/settings/skus">SKU Manager</a>. Unrelated to Profiles. `.jsonc` with
    <code>//</code> and <code>/* */</code> comments is fine — comments are stripped on upload, not stored.
  </p>

  {#if !isStaff}
    <Card title="Staff only">
      <p class="muted">Only admin/staff can manage Command Sets.</p>
    </Card>
  {:else}
    <Card title="Upload Command Set" subtitle="Must include a command with id &quot;ping&quot;">
      <form class="form" onsubmit={(e) => void onUpload(e)}>
        <label class="field">
          <span class="label">Name</span>
          <input class="input" type="text" bind:value={newName} maxlength={120} disabled={saving} required />
        </label>
        <label class="field">
          <span class="label">command.json / command.jsonc file</span>
          <input class="input" type="file" name="file" accept=".json,.jsonc,application/json" disabled={saving} />
        </label>
        {#if formError}
          <p class="err" role="alert">{formError}</p>
        {/if}
        <div class="actions">
          <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Upload'}</Button>
        </div>
      </form>
    </Card>

    <Card title="Library" subtitle={loading ? 'Loading…' : `${commandSets.length} Command Set(s)`}>
      {#if error}
        <p class="err" role="alert">{error}</p>
      {:else if loading}
        <p class="muted">Loading…</p>
      {:else if commandSets.length === 0}
        <p class="muted">No Command Sets yet. Upload a command.json to get started.</p>
      {:else}
        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Commands</th>
                <th>Used by SKUs</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {#each commandSets as cs (cs.id)}
                <tr class:active-row={detailId === cs.id}>
                  <td><div class="id-main">{cs.name}</div></td>
                  <td>{cs.commandCount}</td>
                  <td class="sku-cell">
                    {#if cs.skuCodes.length > 0}
                      <span class="chips">
                        {#each cs.skuCodes.slice(0, 4) as code}
                          <span class="chip">{code}</span>
                        {/each}
                        {#if cs.skuCodes.length > 4}
                          <span class="chip muted-chip">+{cs.skuCodes.length - 4}</span>
                        {/if}
                      </span>
                    {:else}
                      <span class="muted">—</span>
                    {/if}
                  </td>
                  <td class="row-actions">
                    <button type="button" class="linkish" disabled={saving} onclick={() => void openDetail(cs)}
                      >View</button
                    >
                    <button type="button" class="linkish danger" disabled={saving} onclick={() => void onDelete(cs)}
                      >Delete</button
                    >
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </Card>

    {#if detailId}
      <Card title={detailName} subtitle="Read-only JSON · replace file only">
        {#if detailLoading}
          <p class="muted">Loading…</p>
        {:else}
          <pre class="json">{detailJson}</pre>
          <form class="form replace" onsubmit={(e) => void onReplace(e)}>
            <label class="field">
              <span class="label">Rename (optional)</span>
              <input class="input" type="text" bind:value={detailName} maxlength={120} disabled={saving} />
            </label>
            <label class="field">
              <span class="label">Replace file</span>
              <input class="input" type="file" name="replace" accept=".json,.jsonc,application/json" disabled={saving} />
            </label>
            <div class="actions">
              <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Replace file'}</Button>
              <button
                type="button"
                class="linkish muted"
                disabled={saving}
                onclick={() => {
                  detailId = null;
                  detailJson = '';
                }}>Close</button
              >
            </div>
          </form>
        {/if}
      </Card>
    {/if}
  {/if}
</div>

<style>
  .page {
    max-width: 56rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  h2 {
    margin: 0.25rem 0 0;
    font-size: 1.5rem;
  }
  .back {
    font-size: 0.875rem;
    color: var(--text-muted);
    text-decoration: none;
  }
  .back:hover {
    color: var(--text);
  }
  .sub {
    margin: 0.35rem 0 0.5rem;
    color: var(--text-muted);
    font-size: 0.95rem;
    line-height: 1.45;
  }
  .sub a {
    color: var(--accent, #3b82f6);
  }
  .form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .label {
    font-size: 0.8rem;
    color: var(--text-muted);
  }
  .input {
    font: inherit;
    padding: 0.5rem 0.65rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border, #2a2a32);
    background: var(--surface-2, #121218);
    color: var(--text);
  }
  .actions {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }
  .err {
    color: #f87171;
    margin: 0;
    font-size: 0.9rem;
  }
  .muted {
    color: var(--text-muted);
  }
  .table-wrap {
    overflow-x: auto;
  }
  .table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
  }
  th,
  td {
    text-align: left;
    padding: 0.55rem 0.4rem;
    border-bottom: 1px solid var(--border, #2a2a32);
    vertical-align: top;
  }
  th {
    color: var(--text-muted);
    font-weight: 500;
    font-size: 0.8rem;
  }
  .id-main {
    font-weight: 600;
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }
  .chip {
    display: inline-block;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    background: var(--surface-2, #1a1a22);
    border: 1px solid var(--border, #2a2a32);
    font-size: 0.75rem;
  }
  .muted-chip {
    color: var(--text-muted);
  }
  .row-actions {
    white-space: nowrap;
    text-align: right;
  }
  .linkish {
    background: none;
    border: none;
    color: var(--accent, #3b82f6);
    cursor: pointer;
    font: inherit;
    padding: 0.15rem 0.35rem;
  }
  .linkish:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .linkish.danger {
    color: #f87171;
  }
  .linkish.muted {
    color: var(--text-muted);
  }
  .active-row {
    background: color-mix(in srgb, var(--accent, #3b82f6) 8%, transparent);
  }
  .json {
    margin: 0 0 1rem;
    padding: 0.75rem;
    max-height: 22rem;
    overflow: auto;
    border-radius: 0.5rem;
    background: var(--surface-2, #0e0e14);
    border: 1px solid var(--border, #2a2a32);
    font-size: 0.75rem;
    line-height: 1.4;
    white-space: pre-wrap;
    word-break: break-word;
  }
  .replace {
    margin-top: 0.5rem;
  }
</style>
